import { IKey } from '@/common/constants/ikey'
import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'

/** Первый поиск: не найдено. */
export const TREE_TABLE_SEARCH_NOT_FOUND_MSG =
  'Не найдено строк, соответствующих критериям поиска.'
/** «Найти далее» / поиск в обратном направлении (как Swing). */
export const TREE_TABLE_SEARCH_NOT_FOUND_NEXT_MSG =
  'Не найдено строк, соответствующих критериям поиска в указанном направлении.'

export type TreeTableSearchExtracted =
  | { kind: 'none' }
  | {
      kind: 'found'
      pathRows: any[]
      nextSearchPosition: number
      selectedRowNum: number | null
    }
  | {
      kind: 'notFound'
      showMessage: boolean
      nextSearchPosition: number
      /** Как SwingTreeTable.reload: диалог «не найдено» без сброса выделения в дереве. */
      preserveSelection: boolean
    }

/** Состояние клиентской сессии поиска */
export type TreeTableSearchSessionState = {
  lastPathIdentity: string
  lastNextSearchPosition: number
  lastCriteriaKey: string
}

export function createTreeTableSearchSessionState(): TreeTableSearchSessionState {
  return { lastPathIdentity: '', lastNextSearchPosition: -1, lastCriteriaKey: '' }
}

function rowIdFromPathRow(row: unknown): string {
  const id =
    (row as { fields?: { _id?: { value?: unknown } } })?.fields?._id?.value ??
    (row as { _id?: unknown })?._id ??
    ''
  return String(id)
}

/** Идентичность пути _selected (все _id узлов), без nextSearchPosition. */
export function buildTreeTableSearchPathIdentity(pathRows: unknown[]): string {
  if (!pathRows.length) return ''
  return pathRows.map((row) => rowIdFromPathRow(row)).join('>')
}

export type TreeTableSearchCriteriaOptions = {
  /**
   * Live form overrides (Text-input scalars) — авторитетный источник
   * текущего пользовательского ввода поверх pageFields snapshot.
   */
  liveOverrides?: Record<string, { type?: string; value?: unknown } | undefined>
  searchString?: unknown
}

/**
 * Имя поля — служебный суффикс платформы или относится к таблице.
 */
function isPlatformOrTableAuxFieldName(name: string, tableName: string): boolean {
  if (!name) return true
  if (tableName && name === tableName) return true
  if (name.includes('__')) return true
  if (tableName && name.startsWith(`${tableName}_`)) return true
  return false
}

/**
 * Скаляры — кандидаты на «строку поиска».
 */
function collectTreeTableSearchScalarInputs(
  pageFields: Record<string, unknown>,
  tableName: string,
  liveOverrides?: Record<string, { type?: string; value?: unknown } | undefined>,
): Array<[string, string]> {
  const map = new Map<string, string>()
  if (liveOverrides) {
    for (const [k, v] of Object.entries(liveOverrides)) {
      if (isPlatformOrTableAuxFieldName(k, tableName)) continue
      if (!v || typeof v !== 'object') continue
      const entry = v as { type?: string; value?: unknown }
      if (entry.type && entry.type !== 'String') continue
      map.set(k, String(entry.value ?? '').trim())
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
  }
  for (const [k, v] of Object.entries(pageFields)) {
    if (isPlatformOrTableAuxFieldName(k, tableName)) continue
    const entry = v as { type?: string; value?: unknown } | undefined
    if (entry?.type !== 'String') continue
    map.set(k, String(entry.value ?? '').trim())
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
}

export function buildTreeTableSearchCriteriaKey(
  pageFields: Record<string, unknown>,
  tableName: string,
  live?: TreeTableSearchCriteriaOptions,
): string {
  const entries = collectTreeTableSearchScalarInputs(pageFields, tableName, live?.liveOverrides)
  if (live?.searchString !== undefined) {
    const text = String(live.searchString ?? '').trim()
    if (entries.length === 0) {
      if (text !== '') entries.push(['', text])
    } else {
      for (let i = 0; i < entries.length; i++) entries[i] = [entries[i][0], text]
    }
  }
  if (entries.every(([, v]) => v === '')) return '\0'
  const userPart = entries
    .filter(([, v]) => v !== '')
    .map(([k, v]) => `${k}=${v}`)
    .join('|')
  
  return `${userPart}\0`
}

/**
 * Поле — критерий поиска TreeTable.
 */
export function fieldParticipatesInTreeTableSearchCriteria(
  fieldName: string,
  pageFields: Record<string, unknown>,
  tableName: string,
  options?: {
    liveOverrides?: Record<string, { type?: string; value?: unknown } | undefined>
  },
): boolean {
  const name = String(fieldName ?? '').trim()
  if (!name || !tableName) return false
  if (isPlatformOrTableAuxFieldName(name, tableName)) return false
  const overrides = options?.liveOverrides
  if (overrides) {
    return Object.prototype.hasOwnProperty.call(overrides, name)
  }
  const fullKey = buildTreeTableSearchCriteriaKey(pageFields, tableName)
  const reduced = { ...pageFields, [name]: { type: 'String', value: '' } }
  return buildTreeTableSearchCriteriaKey(reduced, tableName) !== fullKey
}

export function isEmptyTreeTableSearchCriteriaKey(criteriaKey: string): boolean {
  return criteriaKey === '\0' || criteriaKey === ''
}

export function criteriaKeySearchText(criteriaKey: string): string {
  if (isEmptyTreeTableSearchCriteriaKey(criteriaKey)) return ''
  const idx = criteriaKey.indexOf('\0')
  return idx >= 0 ? criteriaKey.slice(0, idx) : criteriaKey
}

export function resetTreeTableSearchSessionState(
  session: TreeTableSearchSessionState,
): void {
  session.lastPathIdentity = ''
  session.lastNextSearchPosition = -1
  session.lastCriteriaKey = ''
}

export function commitTreeTableSearchSession(
  target: TreeTableSearchSessionState,
  patch: TreeTableSearchSessionState,
): void {
  target.lastCriteriaKey = patch.lastCriteriaKey
  target.lastPathIdentity = patch.lastPathIdentity
  target.lastNextSearchPosition = patch.lastNextSearchPosition
}

/**
 * Не применять ответ сервера: фильтр очищен, ввод не совпал со snapshot,
 * или snapshot не от последнего отправленного doAction.
 */
export function shouldIgnoreTreeTableSearchResponse(
  liveCriteriaKey: string,
  pageFields: Record<string, unknown>,
  tableName: string,
  submittedCriteriaKey?: string,
  liveOverrides?: Record<string, { type?: string; value?: unknown } | undefined>,
): boolean {
  const snapKey = buildTreeTableSearchCriteriaKey(pageFields, tableName, { liveOverrides })
  const liveText = criteriaKeySearchText(liveCriteriaKey)
  const snapText = criteriaKeySearchText(snapKey)

  if (liveText === '' && snapText !== '') {
    return true
  }

  if (liveText !== '' && snapText !== '' && liveText !== snapText) {
    return true
  }

  if (submittedCriteriaKey !== undefined) {
    const subText = criteriaKeySearchText(submittedCriteriaKey)
    if (submittedCriteriaKey === '\0' && snapText !== '') {
      return true
    }
    if (subText !== '' && snapText !== '' && snapText !== subText) {
      return true
    }
  }

  return false
}

const TREE_TABLE_AUX_FIELD_SUFFIXES = [
  IKey.TT_SEARCH,
  IKey.TT_NEXT_SEARCH_POSITION,
  IKey.SHOW_MESSAGE_DIALOG,
  IKey.SELECTED,
  '__selected',
] as const

function tableNameFromTreeTableAuxField(fieldKey: string): string | null {
  for (const suffix of TREE_TABLE_AUX_FIELD_SUFFIXES) {
    if (fieldKey.endsWith(suffix)) {
      return fieldKey.slice(0, -suffix.length)
    }
  }
  return null
}

/** Поле данных дерева до первого search (ещё нет `{name}__search` в pageFields). */
function looksLikeTreeTableRootField(
  pageFields: Record<string, unknown>,
  fieldKey: string,
): boolean {
  if (!fieldKey || fieldKey.includes('__')) return false
  const entry = pageFields[fieldKey] as { type?: string } | undefined
  if (entry?.type && entry.type !== 'UniDataTable') return false
  const hasRelatedFields = Object.keys(pageFields).some((k) => {
    if (k === fieldKey) return false
    return k.startsWith(`${fieldKey}__`) || k.startsWith(`${fieldKey}_`)
  })
  if (!hasRelatedFields) return false
  return Array.isArray(extractUniDataTableRows(pageFields[fieldKey]))
}

/**
 * Имя TreeTable на форме: по `{name}__search` / _selected / … или по полю `{name}` с rows.
 */
export function resolveTreeTableFieldNameFromPageFields(
  pageFields: Record<string, unknown>,
): string | null {
  for (const key of Object.keys(pageFields)) {
    const fromAux = tableNameFromTreeTableAuxField(key)
    if (fromAux) return fromAux
  }
  for (const key of Object.keys(pageFields)) {
    if (looksLikeTreeTableRootField(pageFields, key)) {
      return key
    }
  }
  return null
}

export function buildTreeTableSearchWatchToken(
  pageFields: Record<string, unknown>,
  tableName: string,
): string {
  if (!isTreeTableSearchExchange(pageFields, tableName)) return ''
  const raw = extractTreeTableSearchResult(pageFields, tableName)
  if (raw.kind === 'none') return ''
  const pos = readLongField(pageFields, `${tableName}${IKey.TT_NEXT_SEARCH_POSITION}`)
  if (raw.kind === 'found') {
    const pathId = buildTreeTableSearchPathIdentity(raw.pathRows)
    return `w:f:${pos}:${pathId}:${raw.pathRows.length}`
  }
  return `w:nf:${pos}:${raw.showMessage ? 1 : 0}`
}

/**
 * Сервер иногда возвращает тот же path с увеличенным nextSearchPosition (конец «найти далее»).
 * Чистая функция — не мутирует session
 */
export function reconcileTreeTableSearchResult(
  extracted: TreeTableSearchExtracted,
  session: TreeTableSearchSessionState,
  criteriaKey: string,
): { result: TreeTableSearchExtracted; session: TreeTableSearchSessionState } {
  const next: TreeTableSearchSessionState = {
    lastPathIdentity: session.lastPathIdentity,
    lastNextSearchPosition: session.lastNextSearchPosition,
    lastCriteriaKey: session.lastCriteriaKey,
  }

  const hadPriorCriteria = next.lastCriteriaKey !== ''
  const criteriaChanged = hadPriorCriteria && criteriaKey !== next.lastCriteriaKey
  if (criteriaKey !== next.lastCriteriaKey) {
    next.lastCriteriaKey = criteriaKey
    next.lastPathIdentity = ''
    next.lastNextSearchPosition = -1
  }

  if (extracted.kind === 'notFound') {
    next.lastPathIdentity = ''
    next.lastNextSearchPosition = -1
    return { result: extracted, session: next }
  }

  if (extracted.kind !== 'found') {
    return { result: extracted, session: next }
  }

  const identity = buildTreeTableSearchPathIdentity(extracted.pathRows)
  const pos = extracted.nextSearchPosition

  if (criteriaChanged && pos > 0) {
    return {
      result: {
        kind: 'notFound',
        showMessage: true,
        nextSearchPosition: pos,
        preserveSelection: true,
      },
      session: next,
    }
  }

  if (pos === 0) {
    next.lastPathIdentity = ''
    next.lastNextSearchPosition = -1
  }

  if (
    next.lastPathIdentity &&
    identity === next.lastPathIdentity &&
    next.lastNextSearchPosition >= 0 &&
    pos === next.lastNextSearchPosition + 1
  ) {
    next.lastPathIdentity = ''
    next.lastNextSearchPosition = -1
    return {
      result: {
        kind: 'notFound',
        showMessage: true,
        nextSearchPosition: pos,
        preserveSelection: true,
      },
      session: next,
    }
  }

  next.lastPathIdentity = identity
  next.lastNextSearchPosition = pos
  return { result: extracted, session: next }
}

export function signatureFromTreeTableSearchResult(
  result: TreeTableSearchExtracted,
  pageFields: Record<string, unknown>,
  tableName: string,
  criteriaKey: string,
): string {
  if (result.kind === 'none') return ''
  if (result.kind === 'notFound') {
    const searchFlag = readLongField(pageFields, `${tableName}${IKey.TT_SEARCH}`)
    return `nf:${criteriaKey}:${result.showMessage}:${searchFlag}`
  }
  const identity = buildTreeTableSearchPathIdentity(result.pathRows)
  return `f:${identity}:${result.pathRows.length}`
}

function readFieldEntry(pageFields: Record<string, unknown>, fieldName: string): unknown {
  const entry = pageFields[fieldName]
  if (entry == null) return undefined
  if (typeof entry === 'object' && entry !== null && 'value' in entry) {
    return (entry as { value: unknown }).value
  }
  return entry
}

function readBooleanField(pageFields: Record<string, unknown>, fieldName: string): boolean | null {
  const raw = readFieldEntry(pageFields, fieldName)
  if (raw === true || raw === false) return raw
  if (raw === 'true' || raw === 1 || raw === '1') return true
  if (raw === 'false' || raw === 0 || raw === '0') return false
  return null
}

function readLongField(pageFields: Record<string, unknown>, fieldName: string): number {
  const raw = readFieldEntry(pageFields, fieldName)
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

function swingSelectedFieldName(tableName: string): string {
  return `${tableName}${IKey.SELECTED}`
}

function altSelectedFieldName(tableName: string): string {
  return `${tableName}__selected`
}

function pathRowsFromField(
  pageFields: Record<string, unknown>,
  fieldName: string,
): any[] {
  if (!(fieldName in pageFields)) return []
  if (readBooleanField(pageFields, fieldName) === false) return []
  return extractUniDataTableRows(readFieldEntry(pageFields, fieldName))
}

/** Ответ reload после поиска, а не «залипшие» флаги от прошлого reload. */
export function isTreeTableSearchExchange(
  pageFields: Record<string, unknown>,
  tableName: string,
): boolean {
  const searchKey = `${tableName}${IKey.TT_SEARCH}`
  const swingSel = swingSelectedFieldName(tableName)
  const altSel = altSelectedFieldName(tableName)

  const swingPath = pathRowsFromField(pageFields, swingSel)
  const altPath = pathRowsFromField(pageFields, altSel)
  if (swingPath.length > 0 || altPath.length > 0) return true

  if (readBooleanField(pageFields, searchKey) === true) return true

  const altSelFalse = readBooleanField(pageFields, altSel) === false
  const showMessage =
    readBooleanField(pageFields, `${tableName}${IKey.SHOW_MESSAGE_DIALOG}`) === true

  if (!showMessage) return false

  // Swing: orgChart__selected=false + showMessageDialog (конец «найти далее»)
  if (altSelFalse) {
    if (readBooleanField(pageFields, swingSel) === false) {
      return readBooleanField(pageFields, searchKey) === true
    }
    return true
  }

  if (readBooleanField(pageFields, swingSel) === false) {
    return readBooleanField(pageFields, searchKey) === true
  }

  return false
}

export function extractTreeTableSearchResult(
  pageFields: Record<string, unknown> | null | undefined,
  tableName: string,
): TreeTableSearchExtracted {
  if (!pageFields || !tableName) return { kind: 'none' }
  if (!isTreeTableSearchExchange(pageFields, tableName)) return { kind: 'none' }

  const nextSearchPosition = readLongField(
    pageFields,
    `${tableName}${IKey.TT_NEXT_SEARCH_POSITION}`,
  )
  const selectedRowNumRaw = readLongField(
    pageFields,
    `${tableName}${IKey.TABLE_SELECTED_ROW}`,
  )
  const selectedRowNum = selectedRowNumRaw >= 0 ? selectedRowNumRaw : null
  const showMessage =
    readBooleanField(pageFields, `${tableName}${IKey.SHOW_MESSAGE_DIALOG}`) === true

  const searchKey = `${tableName}${IKey.TT_SEARCH}`
  const swingSel = swingSelectedFieldName(tableName)
  const altSel = altSelectedFieldName(tableName)
  const swingPath = pathRowsFromField(pageFields, swingSel)
  const altPath = pathRowsFromField(pageFields, altSel)
  const altSelFalse = readBooleanField(pageFields, altSel) === false

  // orgChart__selected с path — авторитетный сигнал «найдено» (выше устаревшего path в orgChart_selected)
  if (altPath.length > 0) {
    return { kind: 'found', pathRows: altPath, nextSearchPosition, selectedRowNum }
  }

  // Выше устаревшего path в orgChart_selected
  if (altSelFalse && showMessage) {
    return {
      kind: 'notFound',
      showMessage: true,
      nextSearchPosition,
      preserveSelection: true,
    }
  }

  if (swingPath.length > 0) {
    return { kind: 'found', pathRows: swingPath, nextSearchPosition, selectedRowNum }
  }

  if (readBooleanField(pageFields, swingSel) === false && showMessage) {
    return {
      kind: 'notFound',
      showMessage: true,
      nextSearchPosition,
      preserveSelection: true,
    }
  }

  if (showMessage && readBooleanField(pageFields, searchKey) === true) {
    return {
      kind: 'notFound',
      showMessage: true,
      nextSearchPosition,
      preserveSelection: true,
    }
  }

  return {
    kind: 'notFound',
    showMessage: false,
    nextSearchPosition,
    preserveSelection: false,
  }
}

export function resolveTreeTableSearchNotFoundMessage(
  nextSearchPosition: number,
  pageFields?: Record<string, unknown> | null,
  tableName?: string,
): string {
  let useDirectionMessage = nextSearchPosition > 0
  if (pageFields && tableName) {
    const direction = readLongField(pageFields, `${tableName}__direction`)
    if (direction < 0 && nextSearchPosition === 0) {
      useDirectionMessage = true
    }
  }
  return useDirectionMessage
    ? TREE_TABLE_SEARCH_NOT_FOUND_NEXT_MSG
    : TREE_TABLE_SEARCH_NOT_FOUND_MSG
}

export function applyPathMergeToTableField(
  existingField: unknown,
  mergedRootRows: any[],
): Record<string, unknown> {
  const base: Record<string, unknown> =
    existingField && typeof existingField === 'object'
      ? { ...(existingField as Record<string, unknown>) }
      : { type: 'UniDataTable' }
  const rawValue = base.value
  const prevValue: Record<string, unknown> =
    rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)
      ? { ...(rawValue as Record<string, unknown>) }
      : {}
  return {
    ...base,
    type: 'UniDataTable',
    value: {
      ...prevValue,
      rows: mergedRootRows,
      columns: Array.isArray(prevValue.columns) ? prevValue.columns : [],
      attributes:
        prevValue.attributes && typeof prevValue.attributes === 'object'
          ? prevValue.attributes
          : {},
      empty: mergedRootRows.length === 0,
    },
  }
}
